// Public share links — the ONE implementation behind the "Share publicly…"
// action on every surface (PUBLIC-SHARE-LINKS-1).
//
// WHY IT LIVES IN THE SHELL, not in files/ and documents/
// Both feature packages need the identical dialog, and neither may import
// the other (no sibling imports between project files). The shell already
// owns the cross-surface chrome, so the modal + these helpers live here once
// and a feature package contributes a single menu row:
//
//   el.call('openShareModal', { targetType: 'file', targetId, name })
//
// Functions and components merge into ONE context at boot, so the call and
// the `extends: 'ShareLinkModal'` bare key resolve from any package mounted
// in the shell. Outside the shell the call is simply absent — the menu row
// degrades to a no-op instead of crashing.
//
// ⚠️ THE TOKEN IS SHOWN ONCE
// The server stores only sha256(token). `createShareLink` is the only call
// that ever returns the raw value, so `_shareNewUrl` holds it for exactly as
// long as the dialog is open and nothing persists it. Reopening the dialog
// lists the link but CANNOT show its URL again — that is the design, not a
// gap: mint a new link and revoke the old one.
//
// Defaults are Nika's product calls (2026-08-20): the link serves the LIVE
// document, and it expires in 30 DAYS unless the user picks "never".

import _wsShared from '@symbo-ls/workspace-shared'

// The SDK comes off the ELEMENT first (`el.sdk`, what every files/notes
// function already uses) and falls back to the shared singleton only when a
// caller has none. Element-first is what makes these functions testable
// without a live SDK — and it is the same instance either way at runtime.
const _shellSdk = (el) => el?.sdk || (_wsShared.functions && _wsShared.functions.getSDK())

const SHARE_LAYER_ID = 'share-link'

// Offered expiries. 30 days is first and is the default the dialog opens on
// — the safe option must be the one the user does not have to choose.
export const SHARE_EXPIRY_CHOICES = [
  { key: '30', days: 30, label: 'workspace.share.expiry.d30' },
  { key: '7', days: 7, label: 'workspace.share.expiry.d7' },
  { key: '90', days: 90, label: 'workspace.share.expiry.d90' },
  { key: 'never', days: null, label: 'workspace.share.expiry.never' }
]

// The ONE shape of a public share-link URL, used by every gate that has to
// recognise one: the shell entry's bootShell `publicSurface` flag
// (index.js), the SPA nav guard (app.js) and the viewer page itself. A
// signed-in member opening their own link from inside the app arrives at
// /w/<org>[/<workspace>]/s/<token>; the recipient it was made for arrives at
// a bare /s/<token>. Both are the viewer.
//
// It lives here rather than in the shared package's canonical guest-route
// regex because that file belongs to another squad — see app.js's guard for
// the full note. Keeping the one definition here is what stops the two shell
// gates from drifting apart.
export const isPublicShareViewPath = (pathname) =>
  /^(?:\/w\/[a-z0-9-]{1,48}(?:\/[A-Za-z0-9_-]{1,64})?)?\/s\/[^/]+/.test(
    typeof pathname === 'string' ? pathname : ''
  )

const _rootState = (el) =>
  el?.state?.root || el?.getRoot?.()?.state?.root || el?.getRoot?.()?.state || null

const _origin = () => (typeof window !== 'undefined' ? window.location.origin : '')

// Two busy flags, not one. `_shareLoading` is the BACKGROUND list read that
// openShareModal kicks off; `_shareBusy` is a user-initiated mutation
// (create/revoke). Sharing one flag made the dialog's own opening read
// disable the Create button until the list came back — a real dead window
// on a slow link, and the reason the unit suite could not create right
// after opening.
const _reset = {
  _shareTarget: null,
  _shareLinks: [],
  _shareBusy: false,
  _shareLoading: false,
  _shareError: null,
  _shareNewUrl: null,
  _shareExpiryKey: '30'
}

// The absolute URL a person pastes into a message. Composed through the SDK
// so the server-returned path and this app's own origin are joined in one
// place — the API deliberately never returns an absolute URL (it must not
// carry a deployment identity).
export function shareLinkUrl(pathOrToken) {
  if (!pathOrToken) return ''
  // Composed HERE rather than through `sdk.shareUrl`, even though the SDK
  // ships the identical helper. The shell reaches the SDK through
  // shared/functions/sdkClient.js, whose proxy makes EVERY method async
  // unless it is in the small `SYNC_PASS` allowlist — so a SYNC SDK helper
  // called from UI code returns a Promise, and a Promise stringifies into
  // the DOM as "[object Promise]". Measured live 2026-08-22 on
  // workspace.localhost: `sdk.shareUrl('/s/ABC', origin)` came back as a
  // Promise. The allowlist lives in a shared package this squad does not
  // own, and a URL join needs no SDK at all — so the shell owns the join
  // and the SDK method stays for consumers that call the SDK directly.
  const path = String(pathOrToken).startsWith('/s/') ? String(pathOrToken) : `/s/${pathOrToken}`
  const origin = _origin()
  return origin ? `${origin.replace(/\/+$/, '')}${path}` : path
}

export function openShareModal(target) {
  const root = _rootState(this)
  if (!root || !target || !target.targetType || !target.targetId) return
  root.update({
    ..._reset,
    _shareTarget: {
      targetType: target.targetType,
      targetId: String(target.targetId),
      name: target.name || ''
    }
  })
  this.call('openCustomModal', {
    id: SHARE_LAYER_ID,
    content: { extends: 'ShareLinkModal' }
  })
  this.call('refreshShareLinks')
}

export function closeShareModal() {
  const root = _rootState(this)
  root?.update(_reset)
  this.call('closeModal', SHARE_LAYER_ID)
}

// The links this resource already has out. Read on open so the dialog can
// answer "have I shared this before, and is that link still live?" — the
// question that makes an informed revoke possible.
export async function refreshShareLinks() {
  const root = _rootState(this)
  const target = root?._shareTarget
  if (!target) return
  root.update({ _shareLoading: true, _shareError: null })
  try {
    const res = await _shellSdk(this).listShareLinks({
      targetType: target.targetType,
      targetId: target.targetId
    })
    root.update({ _shareLoading: false, _shareLinks: res?.data || res || [] })
  } catch (err) {
    root.update({
      _shareLoading: false,
      _shareError: err?.message || this.call('polyglot', 'workspace.share.error.loadFailed')
    })
  }
}

export async function createShareLinkNow() {
  const root = _rootState(this)
  const target = root?._shareTarget
  if (!target || root._shareBusy) return
  const choice =
    SHARE_EXPIRY_CHOICES.find((c) => c.key === (root._shareExpiryKey || '30')) ||
    SHARE_EXPIRY_CHOICES[0]
  root.update({ _shareBusy: true, _shareError: null })
  try {
    // `days: null` means never — pass it EXPLICITLY. Omitting the key would
    // ask the server for its default instead, which is the opposite choice.
    const created = await _shellSdk(this).createShareLink({
      targetType: target.targetType,
      targetId: target.targetId,
      expiresInDays: choice.days
    })
    const url = shareLinkUrl(created?.path || created?.token)
    root.update({ _shareBusy: false, _shareNewUrl: url })
    await this.call('refreshShareLinks')
    // The clipboard write is FIRE-AND-FORGET, deliberately not awaited.
    // `navigator.clipboard.writeText` can HANG — not reject — while the
    // browser decides whether to prompt for the permission (measured live
    // 2026-08-22: a create in a non-focused tab left the await pending long
    // enough to time out the whole evaluation). Awaiting it would leave the
    // dialog mid-flow on a link that already exists and is already on
    // screen. The copy is a convenience; the link is the result.
    void this.call('copyShareLink', url)
  } catch (err) {
    root.update({
      _shareBusy: false,
      _shareError: err?.message || this.call('polyglot', 'workspace.share.error.createFailed')
    })
  }
}

export async function copyShareLink(url) {
  if (!url) return false
  try {
    await navigator.clipboard.writeText(url)
    this.call('openNotification', {
      type: 'success',
      title: this.call('polyglot', 'workspace.share.copied.title'),
      message: this.call('polyglot', 'workspace.share.copied.message')
    })
    return true
  } catch (err) {
    // A clipboard refusal is not a failure of the SHARE — the link exists and
    // is visible in the dialog. Say exactly that instead of implying the
    // share did not happen.
    this.call('openNotification', {
      type: 'error',
      title: this.call('polyglot', 'workspace.share.copyFailed.title'),
      message: this.call('polyglot', 'workspace.share.copyFailed.message')
    })
    console.warn('[ShareLink] clipboard write failed:', err?.message || err)
    return false
  }
}

export async function revokeShareLinkNow(id) {
  const root = _rootState(this)
  if (!id || !root || root._shareBusy) return
  root.update({ _shareBusy: true, _shareError: null })
  try {
    await _shellSdk(this).revokeShareLink(id)
    // A revoked link's URL must stop being offered for copying — the field
    // still holding it would invite pasting a dead link.
    root.update({ _shareBusy: false, _shareNewUrl: null })
    await this.call('refreshShareLinks')
    this.call('openNotification', {
      type: 'success',
      title: this.call('polyglot', 'workspace.share.revoked.title'),
      message: this.call('polyglot', 'workspace.share.revoked.message')
    })
  } catch (err) {
    root.update({
      _shareBusy: false,
      _shareError: err?.message || this.call('polyglot', 'workspace.share.error.revokeFailed')
    })
  }
}

// The row's Revoke asks first (CRUD standard rule 3, D1): the link stops
// working at once for everyone who has it, and a revoked link cannot come
// back. The title names the item; the message is that fact. In Georgian
// "Revoke" and "Cancel" are one word (გაუქმება), so the dialog's other button
// says "Keep" — two buttons never share a word (D13).
export function confirmRevokeShareLink(id) {
  const root = _rootState(this)
  if (!id || !root || root._shareBusy) return
  const name = String(root._shareTarget?.name || '').trim()
  this.call('confirmDialog', {
    title: name
      ? this.call('polyglot', 'workspace.share.revoke.title').replace('{name}', () => name)
      : this.call('polyglot', 'shell.invite.link.revokeAsk'),
    message: this.call('polyglot', 'workspace.share.revoked.message'),
    confirmText: this.call('polyglot', 'workspace.share.modal.revoke'),
    cancelText: this.call('polyglot', 'shell.invite.link.keep'),
    confirmTheme: 'primary',
    // `null` is the explicit opt-out; OMITTING the key takes the template's
    // 'warning' default.
    icon: null,
    onConfirm: () => this.call('revokeShareLinkNow', id)
  })
}

// Human wording for a row's state. One place, so the list and any future
// surface never drift into describing the same row differently.
export function shareLinkStatusLabel(row) {
  if (!row) return ''
  const t = (k) => this.call('polyglot', k)
  if (row.revokedAt) return t('workspace.share.status.revoked')
  if (row.expiresAt && new Date(row.expiresAt).getTime() <= Date.now())
    return t('workspace.share.status.expired')
  if (!row.expiresAt) return t('workspace.share.status.never')
  const days = Math.max(0, Math.ceil((new Date(row.expiresAt).getTime() - Date.now()) / 86400000))
  return days <= 1
    ? t('workspace.share.status.today')
    : t('workspace.share.status.days').replace('{count}', days)
}
